import type { ETS } from "./ets.js";
import type { ETSPlugin } from "./plugin-api.js";

/**
 * Global manifest shape read by {@link loadModules}. Populated either by the
 * interceptor (CF Worker injects a <script> that sets this before the SDK
 * bundle runs) or manually in dev/test harnesses. Mirrors the structure of
 * `sdks/web/dist/manifest.json` produced by `build.mjs`, with an optional
 * `enabled` flag and per-module `config` layered on top.
 */
export interface ModuleManifest {
  version: string;
  modules: Record<
    string,
    {
      url: string;
      /**
       * 8-char sha256 prefix emitted by `build.mjs`. Diagnostic fingerprint
       * only — too short for SRI. Real integrity enforcement lives in
       * `integrity` below.
       */
      hash?: string;
      /**
       * Subresource Integrity hash, formatted per W3C SRI spec
       * (e.g. `sha384-<base64>`). When present, the loader sets it on
       * `script.integrity` and switches the request to CORS
       * (`script.crossOrigin = "anonymous"`) so the browser can verify the
       * payload before executing it. Missing → loader skips SRI (legacy
       * manifest / dev harness).
       */
      integrity?: string;
      enabled?: boolean;
      config?: Record<string, unknown>;
    }
  >;
}

export interface LoadModulesOptions {
  /**
   * Per-module script load timeout. Modules that neither resolve `onload`
   * nor `onerror` within this window are marked as failed and their name
   * is recorded in the returned `failed` array. Defaults to 10_000ms.
   */
  timeoutMs?: number;
  /**
   * Override the global manifest source. Primarily for tests — production
   * code reads `window.__ETS_MODULE_MANIFEST__`.
   */
  manifest?: ModuleManifest;
}

export interface LoadModulesResult {
  loaded: string[];
  failed: string[];
}

declare global {
  interface Window {
    /**
     * Runtime manifest of optional SDK modules. Injected by the interceptor
     * (prod) or the dev test harness. Missing/undefined is a no-op — the
     * lite bundle stays lite. See {@link ModuleManifest}.
     */
    __ETS_MODULE_MANIFEST__?: ModuleManifest;
    /**
     * Module plugins self-register here on load. The loader reads
     * `__ETS_PLUGINS__[name]` after `onload` and calls `ets.use(plugin)`.
     */
    __ETS_PLUGINS__?: Record<string, unknown>;
  }
}

const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Tracks URLs that have already been injected as <script> tags by this
 * loader in the current page. Prevents double-loading when `loadModules`
 * is called more than once (SPA re-init, hot-reload, etc.) or when a page
 * re-enters the bootstrap path.
 */
const loadedUrls = new Set<string>();

function isPluginLike(value: unknown): value is ETSPlugin {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.name === "string" && typeof obj.init === "function";
}

function injectScript(
  url: string,
  hash: string | undefined,
  integrity: string | undefined,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(false);
      return;
    }
    const script = document.createElement("script");

    // SRI: when the manifest carries a full digest (e.g. `sha384-<base64>`,
    // emitted by build.mjs), enforce it before assigning `src`. SRI on
    // <script> requires the request to be CORS-enabled — set
    // `crossOrigin = "anonymous"` so the browser sends `Origin` and
    // honors `Access-Control-Allow-Origin` from the CDN. The CDN MUST
    // serve modules with permissive CORS headers; otherwise the script
    // will be blocked with a CORS error rather than executing.
    //
    // Order matters: integrity / crossOrigin must be set BEFORE src so
    // they apply to the actual fetch.
    if (integrity) {
      script.integrity = integrity;
      script.crossOrigin = "anonymous";
    }

    script.src = url;
    script.async = true;

    // The 8-char `hash` is just a diagnostic fingerprint (cache-busting,
    // observability) — it's too short for SRI. Real integrity verification
    // lives on `script.integrity` above.
    if (hash) {
      script.dataset.etsHash = hash;
    }

    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      resolve(ok);
    };

    const timer = setTimeout(() => done(false), timeoutMs);

    script.onload = () => done(true);
    script.onerror = () => done(false);

    (document.head || document.documentElement).appendChild(script);
  });
}

/**
 * Reads `window.__ETS_MODULE_MANIFEST__`, injects a <script> tag for each
 * enabled module, waits for it to self-register on `window.__ETS_PLUGINS__`,
 * and hands the plugin to `ets.use(...)`.
 *
 * Fire-and-forget by design: the caller doesn't need to await. The returned
 * promise is exposed for tests and debug tooling.
 *
 * Safe when:
 *   - manifest is absent (noop)
 *   - document/window are absent (SSR guard)
 *   - a module errors or times out (isolated, doesn't block siblings)
 */
export async function loadModules(
  ets: Pick<ETS, "use">,
  options?: LoadModulesOptions,
): Promise<LoadModulesResult> {
  const result: LoadModulesResult = { loaded: [], failed: [] };

  const manifest =
    options?.manifest ??
    (typeof window !== "undefined" ? window.__ETS_MODULE_MANIFEST__ : undefined);

  if (!manifest || !manifest.modules) return result;

  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const entries = Object.entries(manifest.modules).filter(([, spec]) => spec.enabled !== false);

  await Promise.all(
    entries.map(async ([name, spec]) => {
      if (loadedUrls.has(spec.url)) {
        // Already injected — still try to register any plugin that landed
        // under this name (the <script> may have loaded on a previous call).
        registerPlugin(ets, name, result);
        return;
      }
      loadedUrls.add(spec.url);

      const ok = await injectScript(spec.url, spec.hash, spec.integrity, timeoutMs);
      if (!ok) {
        result.failed.push(name);
        if (typeof console !== "undefined" && typeof console.warn === "function") {
          console.warn(`[ets] module ${name} failed to load from ${spec.url}`);
        }
        return;
      }
      registerPlugin(ets, name, result);
    }),
  );

  return result;
}

function registerPlugin(ets: Pick<ETS, "use">, name: string, result: LoadModulesResult): void {
  const plugins = typeof window !== "undefined" ? window.__ETS_PLUGINS__ : undefined;
  const plugin = plugins?.[name];
  if (isPluginLike(plugin)) {
    try {
      ets.use(plugin);
      result.loaded.push(name);
    } catch {
      // ets.use() swallows plugin init errors internally (see ets.ts:129),
      // but guard here anyway in case the call itself throws synchronously.
      result.failed.push(name);
    }
  } else {
    // Script loaded but never registered under this name — treat as failure
    // so operators can spot manifest/bundle drift.
    result.failed.push(name);
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(`[ets] module ${name} loaded but did not register on window.__ETS_PLUGINS__`);
    }
  }
}

/** @internal — exposed for tests to reset loader state between runs. */
export function _resetLoadedUrls(): void {
  loadedUrls.clear();
}
